import React, { useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import useDialogSubmit from 'shared/hooks/useDialogSubmit';

interface BaseProps { open: boolean; onClose: () => void }

export function ResolveDialog({ open, onClose, onResolve, headline }: BaseProps & {
  onResolve: (note: string | null, kind: 'platform_change' | 'offline_change') => Promise<void>; headline: string;
}) {
  const [note, setNote] = useState('');
  const [kind, setKind] = useState<'platform_change' | 'offline_change'>('platform_change');
  const { busy, error, submit } = useDialogSubmit(onClose);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Mark done: {headline}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField autoFocus multiline minRows={2} label="What did you change? (optional, feeds the change log)"
          value={note} onChange={e => setNote(e.target.value)} />
        <ToggleButtonGroup exclusive size="small" value={kind} onChange={(_, v) => v && setKind(v)}>
          <ToggleButton value="platform_change">Platform change (in Google Ads / WordPress)</ToggleButton>
          <ToggleButton value="offline_change">Offline / other</ToggleButton>
        </ToggleButtonGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={busy} onClick={() => submit(() => onResolve(note.trim() || null, kind))}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}

// verb and label: the action steps' Reject (ActionSteps.tsx) is the same dialog in other words.
export function DismissDialog({ open, onClose, onDismiss, headline, verb = 'Dismiss', label = 'Why is no change needed? (optional)' }: BaseProps & {
  onDismiss: (reason: string | null) => Promise<void>; headline: string; verb?: string; label?: string;
}) {
  const [reason, setReason] = useState('');
  const { busy, error, submit } = useDialogSubmit(onClose);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{verb}: {headline}</DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField autoFocus fullWidth multiline minRows={2} label={label}
          value={reason} onChange={e => setReason(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="error" variant="contained" disabled={busy} onClick={() => submit(() => onDismiss(reason.trim() || null))}>{verb}</Button>
      </DialogActions>
    </Dialog>
  );
}

export function SnoozeDialog({ open, onClose, onSnooze, headline }: BaseProps & {
  onSnooze: (until: string) => Promise<void>; headline: string;
}) {
  const [until, setUntil] = useState('');
  const { busy, error, submit } = useDialogSubmit(onClose);
  const preset = (days: number) => {
    const d = new Date(Date.now() + days * 86_400_000);
    setUntil(d.toISOString().slice(0, 10));
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Snooze: {headline}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <div>
          <Button size="small" onClick={() => preset(1)}>Tomorrow</Button>
          <Button size="small" onClick={() => preset(7)}>1 week</Button>
          <Button size="small" onClick={() => preset(30)}>1 month</Button>
        </div>
        <TextField autoFocus type="date" label="Until" InputLabelProps={{ shrink: true }}
          value={until} onChange={e => setUntil(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!until || busy} onClick={() => submit(() => onSnooze(until))}>Snooze</Button>
      </DialogActions>
    </Dialog>
  );
}

